import { type MinimalSubscriptionResponse, type Quote, QUOTE, useObservableValue, useSubscription } from '@talos/kyoko';
import { useMemo } from 'react';
import { map, type Observable } from 'rxjs';
import { calculateBestQuotes, type RfqBestQuoteBy } from '../../../../utils/rfq';
import { CompositeQuoteRow, LegQuoteRow, type QuoteBreakdownRow } from './rows';

interface UseQuoteBreakdownObsParams {
  /** Required to pass rfqID. Allow it to be undefined just for ease of use. */
  rfqID: string | undefined;
  bestBy?: RfqBestQuoteBy;
}

export const useQuoteBreakdownObs = ({ rfqID, bestBy }: UseQuoteBreakdownObsParams) => {
  const quoteRequest = useMemo(
    () =>
      rfqID
        ? {
            name: QUOTE,
            tag: 'takerQuoteBreakdown - useQuoteBreakdownObs',
            RFQID: rfqID,
          }
        : null,
    [rfqID]
  );

  const { data, error } = useSubscription<Quote>(quoteRequest);

  // Grab the last quote sent to us in the message (in case there are several in the .data array)
  const quoteObs = useMemo(() => data.pipe(map(json => json.data.at(-1))), [data]);

  // This observable maps from Quote -> breakdown rows array
  const dataObservable: Observable<MinimalSubscriptionResponse<QuoteBreakdownRow>> = useMemo(
    () =>
      quoteObs.pipe(
        map(quote => {
          if (!quote) {
            return []; // maybe just dont emit....
          }

          // Just returning an array of quote markets for now
          const quoteMarketsWithBestBidOffer = calculateBestQuotes({
            quotes: quote.Markets,
            pricingMode: quote.PricingMode,
            onlyActive: false,
            bestBy,
          });
          const compositeQuoteRows = quoteMarketsWithBestBidOffer.map(
            quoteMarket => new CompositeQuoteRow(quote, quoteMarket)
          );
          const legQuoteRows = quote.Legs?.map(leg => new LegQuoteRow(quote, leg)) ?? [];
          return [...compositeQuoteRows, ...legQuoteRows];
        }),
        map(rows => ({
          data: rows,
          initial: true,
        }))
      ),
    [quoteObs, bestBy]
  );

  const quote = useObservableValue(() => quoteObs, [quoteObs]);
  return { quote, dataObservable, errorObservable: error };
};
